// ════════════════════════════════════════════════════════════════════
// Locale detection
//
// Source of truth for "is this a German user?" + "what currency
// should we display?". Two layers:
//
//   1. User override (highest priority): localStorage 'alma-language',
//      set by the Settings page. Values: 'de' | 'en' | 'auto'
//   2. Browser auto-detect (fallback): navigator.language
//
//   de-CH       → DE language, CHF currency
//   de-DE/AT/-* → DE language, EUR currency
//   de          → DE language, EUR currency
//   en-*        → EN language, USD currency
//   anything    → EN language, USD currency
//
// All checks are SSR-safe (navigator/window may be undefined during build).
// ════════════════════════════════════════════════════════════════════

export type Language = 'en' | 'de';
export type LanguageSetting = Language | 'auto';
export type Currency = 'USD' | 'EUR' | 'CHF';

const LANGUAGE_OVERRIDE_KEY = 'alma-language';

function rawLang(): string {
  if (typeof navigator === 'undefined') return 'en';
  return (navigator.language || 'en').toLowerCase();
}

// Read the user's stored preference. 'auto' = no override = use browser.
export function getLanguageSetting(): LanguageSetting {
  if (typeof window === 'undefined') return 'auto';
  try {
    const stored = window.localStorage.getItem(LANGUAGE_OVERRIDE_KEY);
    if (stored === 'de' || stored === 'en') return stored;
  } catch {
    /* localStorage blocked (e.g. private mode in some browsers) — fall through */
  }
  return 'auto';
}

// Persist the user's choice and reload so every component re-renders
// with the new language. The reload is intentional: avoids subscribing
// every existing isGerman() call site to a React context.
export function setLanguageSetting(value: LanguageSetting): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === 'auto') {
      window.localStorage.removeItem(LANGUAGE_OVERRIDE_KEY);
    } else {
      window.localStorage.setItem(LANGUAGE_OVERRIDE_KEY, value);
    }
  } catch {
    /* swallow — at worst the override won't persist */
  }
  // Soft reload so all components pick up the new language consistently.
  window.location.reload();
}

// Resolves 'auto' to the browser-detected language. Always returns
// a concrete 'de' or 'en'.
export function getLanguage(): Language {
  const setting = getLanguageSetting();
  if (setting === 'de' || setting === 'en') return setting;
  return rawLang().startsWith('de') ? 'de' : 'en';
}

export function isGerman(): boolean {
  return getLanguage() === 'de';
}

// Currency follows the browser locale (physical region), not the
// language toggle. A user who picks German UI on a US browser still
// sees USD — their payment method is in USD. Keeping these decoupled
// avoids a German UI quoting EUR while Stripe charges USD.
export function getCurrency(): Currency {
  const lang = rawLang();
  if (lang.startsWith('de-ch')) return 'CHF';
  if (lang.startsWith('de')) return 'EUR';
  return 'USD';
}

// Units follow the same physical-region signal as currency: a US browser
// gets pounds/ounces/inches/°F first, everyone else metric. Stored values
// stay metric (grams) — this only changes input and display.
export type UnitSystem = 'metric' | 'imperial';

export function getUnitSystem(): UnitSystem {
  return getCurrency() === 'USD' ? 'imperial' : 'metric';
}

export const GRAMS_PER_POUND = 453.59237;
export const GRAMS_PER_OUNCE = 28.349523125;

export function gramsToLbOz(grams: number): { lb: number; oz: number } {
  let lb = Math.floor(grams / GRAMS_PER_POUND);
  let oz = Math.round((grams - lb * GRAMS_PER_POUND) / GRAMS_PER_OUNCE);
  if (oz === 16) {
    lb += 1;
    oz = 0;
  }
  return { lb, oz };
}

export function lbOzToGrams(lb: number, oz: number): number {
  return Math.round(lb * GRAMS_PER_POUND + oz * GRAMS_PER_OUNCE);
}

// Formatted display for the pricing card. Uses local conventions:
// German locales write 9,99 (comma decimal); USD uses 9.99. CHF gets
// a space after the symbol, matching Swiss style.
export interface PriceDisplay {
  monthly: string;
  annual: string;
  annualSavings: string;
}

export function getPriceDisplay(): PriceDisplay {
  switch (getCurrency()) {
    case 'EUR':
      return {
        monthly: '€9,99',
        annual: '€79,99',
        annualSavings: 'Spare 33 % mit Jahresabo — €79,99/Jahr.',
      };
    case 'CHF':
      return {
        monthly: 'CHF 9.99',
        annual: 'CHF 79.99',
        annualSavings: 'Spare 33 % mit Jahresabo — CHF 79.99/Jahr.',
      };
    case 'USD':
    default:
      return {
        monthly: '$9.99',
        annual: '$79.99',
        annualSavings: 'Or save 33% with annual billing — $79.99/year.',
      };
  }
}
